import { z } from 'zod';

/**
 * Wire schema for `GET /api/accounts/:accountId/workspaces` — the Bucket E
 * Workspaces accordion (Paid/Trial tables) on the account view.
 *
 * Source: `account_activity` rows with `source = 'workspace_created'`,
 * decoded via `WorkspaceCreatedPayloadSchema`
 * (`src/shared/schemas/timeline-entry.ts`). This file only defines the
 * OUTPUT shape of the classify/sort step; the payload decode stays on the
 * shared schema so both the timeline and this endpoint read one contract.
 *
 * Classification (verified against live SFDC FlexiPage metadata 2026-08-28 —
 * see `src/api/account-workspaces.ts`'s header comment for the full history;
 * supersedes the plan's original ARR-based Key Technical Decision 5):
 *   - paid  ⇔ `productTier` non-null (mirrors SFDC's own "Paid Workspaces"
 *     related-list filter — a fixed, complete picklist value set, no ARR
 *     involved)
 *   - trial ⇔ not paid AND `lastTrialStartDate` non-null
 *   - anything else (no tier, no trial-start date) is OMITTED from both
 *     arrays — but counted in `omittedCount` so the gap is visible rather
 *     than silent.
 *
 * Schema is the source of truth; TS types below are `z.infer` only
 * (`terra/no-parallel-schema-and-type`).
 */

export const AccountWorkspaceSchema = z.object({
  /** `account_activity.source_id` — the SF `Workspace__c` record id. */
  id: z.string(),
  /** Clay app workspace id (the `app.clay.com/workspaces/{id}` link target). */
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  workspaceUrl: z.string().nullable(),
  /** Drives the paid/trial split (non-null ⇒ paid) — see module doc. */
  productTier: z.string().nullable(),
  subscriptionArr: z.number().nullable(),
  numberOfClayUsers: z.number().nullable(),
  activeStatus: z.string().nullable(),
  activeTrial: z.boolean(),
  /** Sort key for the trial table (desc). */
  lastTrialStartDate: z.string().nullable(),
  lastTrialEndDate: z.string().nullable(),
  hasChatGpt: z.boolean(),
  chatGptConnectionDate: z.string().nullable(),
  creatorName: z.string().nullable(),
});
export type AccountWorkspace = z.infer<typeof AccountWorkspaceSchema>;

/**
 * Response for `GET /api/accounts/:accountId/workspaces`.
 *
 * `hasAccess: false` mirrors the account-timeline endpoint's contract —
 * returned (with HTTP 200) when the account id is unknown, never a 403/404.
 */
export const AccountWorkspacesResponseSchema = z.object({
  paid: z.array(AccountWorkspaceSchema),
  trial: z.array(AccountWorkspaceSchema),
  /**
   * Count of decoded `workspace_created` rows that classified as neither
   * paid nor trial (Decision 5 noise rows — e.g. Active/Enterprise tier with
   * null ARR and null trial-start). Rows dropped for payload drift are NOT
   * counted here (they are logged by `rowToWorkspace`).
   */
  omittedCount: z.number().int().min(0),
  hasAccess: z.boolean(),
});
export type AccountWorkspacesResponse = z.infer<typeof AccountWorkspacesResponseSchema>;
